/**
 * @license
 * Copyright Paysafe 2026. All rights reserved.
 */

import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PsButtonModule } from '@paysafe-ui/components/button';
import { combineLatest, Observable } from 'rxjs';

import { DEFAULT_SUCCESS_ILLUSTRATION, SUCCESS_ILLUSTRATION_MAP } from '../../core/constants/checkout-success-maps.const';
import { CheckoutSuccessType } from '../../core/enums/checkout-success-type.enum';
import { SuccessState } from '../../models/ui/success-state.model';
import { UIStateService } from '../../services/ui-state/ui-state.service';

@Component({
  selector: 'app-checkout-success',
  imports: [CommonModule, PsButtonModule, TranslateModule],
  templateUrl: './checkout-success.component.html',
  styleUrl: './checkout-success.component.scss'
})
export class CheckoutSuccessComponent implements OnInit {

  illustration = '';
  title = '';
  message = '';
  buttonText = '';
  private redirectPath = '';
  private lastSuccessState: SuccessState | null = null;

  constructor(
    private uiStateService: UIStateService,
    private translateService: TranslateService,
    private router: Router,
    private destroyRef: DestroyRef
  ) {
    // Angular DI
  }

  ngOnInit(): void {
    this.uiStateService.successState$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(successState => {
        this.lastSuccessState = successState;
        this.updateComponentState(successState);
      });

    // Re-apply translations when they load (handles async loading)
    this.translateService.onTranslationChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.lastSuccessState) {
          this.updateComponentState(this.lastSuccessState);
        }
      });

    this.destroyRef.onDestroy(() => {
      this.uiStateService.clearSuccess();
    });
  }

  private updateComponentState(successState: SuccessState): void {
    const type = successState.type || CheckoutSuccessType.PAYMENT_COMPLETED;
    const metadata = successState.metadata;
    const i18nKey = `CHECKOUT.SUCCESS.${type}`;

    this.illustration = SUCCESS_ILLUSTRATION_MAP[type] || DEFAULT_SUCCESS_ILLUSTRATION;
    this.redirectPath = metadata?.redirectPath || '#';

    const title$ = metadata?.title
      ? new Observable<string>(sub => {
          sub.next(metadata.title);
          sub.complete();
        })
      : this.translateService.get(`${i18nKey}.TITLE`);

    const description$ = metadata?.message
      ? new Observable<string>(sub => {
          sub.next(metadata.message);
          sub.complete();
        })
      : this.translateService.get(`${i18nKey}.DESCRIPTION`);

    const button$ = metadata?.buttonText
      ? new Observable<string>(sub => {
          sub.next(metadata.buttonText);
          sub.complete();
        })
      : this.translateService.get(`${i18nKey}.BUTTON`);

    combineLatest([
      title$,
      description$,
      button$
    ] as [Observable<string>, Observable<string>, Observable<string>])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([title, description, button]) => {
        this.title = title;
        this.message = description;
        this.buttonText = button;
      });
  }

  handleAction(): void {
    if (this.redirectPath) {
      if (this.redirectPath.startsWith('http://') || this.redirectPath.startsWith('https://')) {
        window.location.href = this.redirectPath;
      } else {
        this.router.navigate([this.redirectPath]);
      }
    } else {
      this.router.navigate(['/']);
    }
  }
}
